
import React, { useState, useEffect } from "react";
import api from "../../../chat/api";
import { isPlanLimitError } from "../../../../events/planLimitEvents";
import toast from "react-hot-toast";
import Cookies from "js-cookie";

const AddSectionModal = ({ 
  isOpen, 
  onClose, 
  onAddSection,
  folderId,
  previousIndex,
  refreshAnalysisData 
}) => {
  const [sectionName, setSectionName] = useState("");
  // Subsections are optional — the section can be created on its own.
  const [subsections, setSubsections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const token = localStorage.getItem("token");

  // Only the section name is required; subsections are optional.
  const isSubmitEnabled = () => {
    return sectionName.trim() !== "" && !isLoading && !isSuccess;
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Prevent zoom on focus for mobile devices
  useEffect(() => {
    if (isOpen) {
      const inputs = document.querySelectorAll('input[type="text"], textarea');
      
      const preventZoom = (e) => {
        e.target.style.fontSize = '16px';
      };
      
      inputs.forEach(input => {
        input.addEventListener('focus', preventZoom);
        input.addEventListener('touchstart', preventZoom);
      });
      
      return () => {
        inputs.forEach(input => {
          input.removeEventListener('focus', preventZoom);
          input.removeEventListener('touchstart', preventZoom);
        });
      };
    }
  }, [isOpen]);

  // Briefly show the success checkmark, then close and refresh. Kept short so the
  // modal doesn't appear stuck after the section has already been added.
  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        setIsSuccess(false);
        resetForm();
        onClose();
        if (refreshAnalysisData) {
          refreshAnalysisData();
        }
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [isSuccess, onClose, refreshAnalysisData]);

  const resetForm = () => {
    setSectionName("");
    setSubsections([]);
    setIsLoading(false);
    setIsSuccess(false);
  };

  if (!isOpen) return null;

  const handleAddSubsection = () => {
    setSubsections([...subsections, { id: Date.now(), title: "", aiPrompt: "" }]);
  };

  const handleRemoveSubsection = (id) => {
    setSubsections(subsections.filter((sub) => sub.id !== id));
  };

  const handleSubsectionChange = (id, field, value) => {
    setSubsections(
      subsections.map((sub) => (sub.id === id ? { ...sub, [field]: value } : sub))
    );
  };

  const handleSubmit = async () => {
    if (!sectionName.trim()) {
      return;
    }

    const validSubsections = subsections.filter(
      (sub) => sub.title.trim() || sub.aiPrompt.trim()
    );

    const subsectionTitles = validSubsections.map(sub => sub.title.trim());
    const subsectionQueries = validSubsections.map(sub => sub.aiPrompt.trim());

    // Prepare request body according to API spec
    const requestBody = {
      previous_index: previousIndex,
      section_title: sectionName.trim(),
      subsection_titles: subsectionTitles,
      subsection_queries: subsectionQueries
    };

    setIsLoading(true);

    try {
      const organizationId = Cookies.get("selected_org_id");
      const url = organizationId
        ? `api/v1/rfp/folders/${folderId}/analysis/add-section/?organization_id=${encodeURIComponent(organizationId)}`
        : `api/v1/rfp/folders/${folderId}/analysis/add-section/`;

      const response = await api.post(
        url,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        const result = response.data;
        console.log('Section added successfully:', result);
        
        // Call the original onAddSection if provided
        if (onAddSection) {
          onAddSection({
            sectionName: sectionName.trim(),
            subsections: validSubsections,
            apiResponse: result
          });
        }
        
        // First set success to true
        setIsSuccess(true);
        // THEN set loading to false
        setIsLoading(false);
        
        // DO NOT reset form here - let the success state show
        // The form will be reset in the useEffect after 3 seconds
        
      } else {
        console.error('Unexpected response status:', response.status);
        throw new Error(`Server returned status ${response.status}`);
      }
    } catch (error) {
      console.error('Error adding section:', error);
      
      let errorMessage = "Failed to add section";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      if (!isPlanLimitError(error)) toast.error(errorMessage);
      setIsLoading(false);
      setIsSuccess(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  // Common input styles
  const inputStyles = "w-full px-4 py-2.5 border font-normal border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C95EBE] focus:border-transparent font-outfit text-sm min-h-[40px] box-border transition-all duration-200";
  const textareaStyles = "w-full px-4 py-2.5 border font-normal border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C95EBE] focus:border-transparent font-outfit text-sm resize-none min-h-[40px] box-border transition-all duration-200";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-[600px] mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4">
          <h2 className="text-xl font-semibold text-gray-900 font-outfit">Add Section</h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading || isSuccess}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1L13 13M1 13L13 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 max-h-[42vh] overflow-y-auto">
          {/* Section Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2 font-outfit">
              Section Name
            </label>
            <input
              type="text"
              value={sectionName}
              onChange={(e) => setSectionName(e.target.value)}
              placeholder="e.g, Implementation Plan"
              className={inputStyles}
              style={{ fontSize: '14px' }}
              disabled={isLoading || isSuccess}
            />
          </div>

          {/* Sub Sections */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700 font-outfit">
                Sub Sections
              </label>
              <button
                onClick={handleAddSubsection}
                className="flex items-center gap-1.5 !text-[#C95EBE] transition-colors text-sm font-medium font-outfit disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading || isSuccess}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.94922 7.4502H0.949219C0.736719 7.4502 0.558636 7.37828 0.414969 7.23445C0.271136 7.09061 0.199219 6.91245 0.199219 6.69994C0.199219 6.48728 0.271136 6.3092 0.414969 6.1657C0.558636 6.02203 0.736719 5.9502 0.949219 5.9502H5.94922V0.950195C5.94922 0.737695 6.02114 0.559612 6.16497 0.415945C6.3088 0.272112 6.48697 0.200195 6.69947 0.200195C6.91214 0.200195 7.09022 0.272112 7.23372 0.415945C7.37739 0.559612 7.44922 0.737695 7.44922 0.950195V5.9502H12.4492C12.6617 5.9502 12.8398 6.02211 12.9835 6.16595C13.1273 6.30978 13.1992 6.48795 13.1992 6.70045C13.1992 6.91311 13.1273 7.0912 12.9835 7.2347C12.8398 7.37836 12.6617 7.4502 12.4492 7.4502H7.44922V12.4502C7.44922 12.6627 7.3773 12.8408 7.23347 12.9844C7.08964 13.1283 6.91147 13.2002 6.69897 13.2002C6.4863 13.2002 6.30822 13.1283 6.16472 12.9844C6.02105 12.8408 5.94922 12.6627 5.94922 12.4502V7.4502Z"
                    fill="#C95EBE"
                  />
                </svg>
                Add Subsection ({subsections.length})
              </button>
            </div>

            {/* Subsection Items */}
            <div className="space-y-4">
              {subsections.length === 0 && (
                <p className="text-sm text-gray-400 font-outfit italic">
                  No subsections added. This section will be created on its own — use “Add Subsection” to include any.
                </p>
              )}
              {subsections.map((subsection, index) => (
                <div key={subsection.id} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={subsection.title}
                      onChange={(e) =>
                        handleSubsectionChange(subsection.id, "title", e.target.value)
                      }
                      placeholder="Sub section Title"
                      className={`${inputStyles} flex-1`}
                      style={{ fontSize: '14px' }}
                      disabled={isLoading || isSuccess}
                    />
                    <button
                      onClick={() => handleRemoveSubsection(subsection.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors min-h-[40px] box-border flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isLoading || isSuccess}
                      title="Remove subsection"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="13"
                        height="15"
                        viewBox="0 0 13 15"
                        fill="none"
                      >
                        <path
                          d="M2.33479 14.0404C1.92137 14.0404 1.56748 13.8932 1.27311 13.5988C0.978739 13.3044 0.831554 12.9505 0.831554 12.5371V1.98284H0.623665C0.44696 1.98284 0.298874 1.92304 0.179408 1.80343C0.0598027 1.68383 0 1.53567 0 1.35897C0 1.18212 0.0598027 1.03404 0.179408 0.914709C0.298874 0.795243 0.44696 0.735509 0.623665 0.735509H3.74199C3.74199 0.531917 3.81371 0.3584 3.95716 0.214957C4.10046 0.0716528 4.27391 0 4.4775 0H7.9958C8.1994 0 8.37285 0.0716528 8.51615 0.214957C8.65959 0.358400 8.73131 0.531917 8.73131 0.735509H11.8496C12.0263 0.735509 12.1744 0.795312 12.2939 0.914917C12.4135 1.03452 12.4733 1.18268 12.4733 1.35938C12.4733 1.53623 12.4135 1.68431 12.2939 1.80364C12.1744 1.92311 12.0263 1.98284 11.8496 1.98284H11.6418V12.5371C11.6418 12.9505 11.4946 13.3044 11.2002 13.5988C10.9058 13.8932 10.5519 14.0404 10.1385 14.0404H2.33479ZM4.70181 11.1299C4.87852 11.1299 5.0266 11.0702 5.14607 10.9507C5.2654 10.8311 5.32506 10.683 5.32506 10.5063V4.26961C5.32506 4.09291 5.26526 3.94475 5.14565 3.82515C5.02619 3.70568 4.87803 3.64595 4.70119 3.64595C4.52448 3.64595 4.3764 3.70568 4.25693 3.82515C4.1376 3.94475 4.07794 4.09291 4.07794 4.26961V10.5063C4.07794 10.683 4.13767 10.8311 4.25714 10.9507C4.37674 11.0702 4.52497 11.1299 4.70181 11.1299ZM7.77212 11.1299C7.94882 11.1299 8.09691 11.0702 8.21637 10.9507C8.3357 10.8311 8.39537 10.683 8.39537 10.5063V4.26961C8.39537 4.09291 8.33563 3.94475 8.21617 3.82515C8.09656 3.70568 7.94834 3.64595 7.77149 3.64595C7.59479 3.64595 7.4467 3.70568 7.32724 3.82515C7.20791 3.94475 7.14824 4.09291 7.14824 4.26961V10.5063C7.14824 10.683 7.20805 10.8311 7.32765 10.9507C7.44712 11.0702 7.59527 11.1299 7.77212 11.1299Z"
                          fill="#E21919"
                        />
                      </svg>
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 font-outfit">
                      AI Prompt Used
                    </label>
                    <textarea
                      value={subsection.aiPrompt}
                      onChange={(e) =>
                        handleSubsectionChange(subsection.id, "aiPrompt", e.target.value)
                      }
                      placeholder="Add custom instructions for AI to generate subsection..."
                      rows={4}
                      className={textareaStyles}
                      style={{ fontSize: '14px' }}
                      disabled={isLoading || isSuccess}
                    />
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-3 p-4 border-t border-gray-200">
          <button
            onClick={handleCancel}
            className="w-[15%] py-2.5 border border-[#C95EBE] rounded-lg !text-[#C95EBE] font-medium transition-colors font-outfit text-sm min-h-[40px] box-border disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading || isSuccess}
          >
            {isSuccess ? "Close" : "Cancel"}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isSubmitEnabled()}
            className={`w-[25%] py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-outfit text-sm min-h-[40px] box-border ${
              isSuccess 
                ? 'bg-[#C95EBE] !text-white' 
                : 'bg-[#C95EBE] !text-white '
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                <span>Adding…</span>
              </div>
            ) : isSuccess ? (
              <div className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 13l4 4L19 7" />
                </svg>
                <span>Section Added</span>
              </div>
            ) : (
              "Add Sections"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSectionModal;